import { useEffect, useMemo } from "react";
import { FieldValues, useForm } from "react-hook-form";
import cn from "classnames";

import { FormProps, FormContextProps } from "./Form.props";
import styles from "./Form.module.scss";
import { FormContextProvider } from "./Form.context";

/**
 * @deprecated Use `import { Form } from capitalrx-components-2.0/lib/forms` instead.
 */
export function Form<T extends FieldValues = FieldValues>({
  id,
  testId,
  autoComplete,
  defaultValues,
  children,
  onSubmit,
  className,
  isFormLoading = false,
  readOnlyForm = false,
  hiddenSubmit = false,
  keepErrors = false,
  keepIsValid = false,
  methodsRef,
}: FormProps<T>): JSX.Element {
  const methods = useForm({ defaultValues, mode: "onBlur", reValidateMode: "onChange" });

  useEffect(() => {
    if (methodsRef) {
      if (typeof methodsRef === "function") methodsRef(methods);
      else methodsRef.current = methods;
    }

    return () => {
      if (methodsRef) {
        if (typeof methodsRef === "function") methodsRef(null);
        else methodsRef.current = null;
      }
    };
  }, [methodsRef, methods]);

  const {
    control,
    clearErrors,
    getValues,
    register,
    resetField,
    setError,
    setFocus,
    setValue,
    trigger,
    unregister,
    watch,
    handleSubmit,
    getFieldState,
    reset,
    formState,
  } = methods;

  const {
    errors,
    dirtyFields,
    isDirty,
    isSubmitting,
    isSubmitted,
    isSubmitSuccessful,
    isValid,
    isValidating,
    submitCount,
    touchedFields,
    isLoading,
    disabled,
    validatingFields,
    isReady,
  } = formState;

  const formClasses = cn(styles["form-content-wrapper"], className);

  useEffect(() => {
    if (!isFormLoading) {
      reset(defaultValues, { keepErrors, keepIsValid });
    }
  }, [defaultValues, isFormLoading, reset]);

  const submitHandler = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);

  // This is so verbose, but this helps us prevent unnecessary refreshing. We have to copy everything manually because react-hook-form modifies some of the internal references for certain elements, so we need to do this to properly handle the change.
  const formContextValue = useMemo(
    (): FormContextProps<T> => ({
      control,
      clearErrors,
      getValues,
      register,
      resetField,
      setError,
      setFocus,
      setValue,
      trigger,
      unregister,
      watch,
      handleSubmit,
      reset,
      handleSubmitEx: submitHandler,
      getFieldState,
      subscribe: methods.subscribe,
      formState: {
        errors,
        dirtyFields,
        isDirty,
        isSubmitted,
        isSubmitting,
        isSubmitSuccessful,
        isValid,
        isValidating,
        submitCount,
        touchedFields,
        isLoading,
        disabled,
        validatingFields,
        isFormLoading,
        isReady,
      },
      readOnly: readOnlyForm,
    }),
    [
      control,
      clearErrors,
      getValues,
      register,
      resetField,
      setError,
      setFocus,
      setValue,
      trigger,
      unregister,
      watch,
      errors,
      dirtyFields,
      isDirty,
      isSubmitting,
      isSubmitSuccessful,
      isValid,
      isValidating,
      submitCount,
      touchedFields,
      submitHandler,
      readOnlyForm,
      isFormLoading,
      isLoading,
      disabled,
      validatingFields,
      isReady,
    ],
  );

  return (
    <form
      id={id}
      data-testid={testId ? `test__${testId}` : undefined}
      className={formClasses}
      autoComplete={autoComplete}
      onSubmit={submitHandler}
    >
      <FormContextProvider value={formContextValue}>
        {children}

        {hiddenSubmit && <button className="hidden-submit" type="submit" />}
      </FormContextProvider>
    </form>
  );
}
